import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Save, ArrowLeft, Plus, Trash2, Download, ExternalLink, ArrowUp, ArrowDown } from "lucide-react";
import { api, API, formatErr } from "@/lib/api";
import ImagePicker from "@/components/ImagePicker";
import { BG_PRESETS, FONT_HEADINGS, FONT_BODIES, buildPageStyles, ensureProposalFontsLoaded } from "@/lib/proposalThemes";

const DEFAULT_STATS = [
  { platform: "Instagram", followers: "", views: "", interactions: "" },
  { platform: "TikTok", followers: "", views: "", interactions: "" },
  { platform: "YouTube", followers: "", views: "", interactions: "" },
  { platform: "Facebook", followers: "", views: "", interactions: "" },
];

export default function AdminProposalEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("cover");

  useEffect(() => { api.get(`/admin/proposals/${id}`).then(setP); }, [id]);
  useEffect(() => { ensureProposalFontsLoaded(); }, []);
  if (!p) return <div>Cargando...</div>;

  const save = async () => {
    setError("");
    try {
      await api.put(`/admin/proposals/${id}`, p);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError(formatErr(e.response?.data?.detail)); }
  };

  const exportPptx = async () => {
    const token = localStorage.getItem("influcas_token");
    const r = await fetch(`${API}/admin/proposals/${id}/pptx`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `propuesta-${p.slug}.pptx`; a.click(); URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    const token = localStorage.getItem("influcas_token");
    const r = await fetch(`${API}/admin/proposals/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { alert("Error generando PDF"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `propuesta-${p.slug}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  const setF = (k, v) => setP({ ...p, [k]: v });

  return (
    <div data-testid="admin-proposal-editor">
      <button onClick={() => nav("/admin/proposals")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Volver a propuestas</button>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-1">{p.title || "Nueva propuesta"}</h1>
          <p className="text-sm text-muted-foreground">{p.client_name} · Slug: <code className="text-xs">{p.slug}</code> {p.published && <Link to={`/propuesta/${p.slug}`} target="_blank" className="text-primary ml-2 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Ver pública</Link>}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportPptx} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm font-semibold text-sm hover:border-primary"><Download className="w-4 h-4" /> PPTX</button>
          <button onClick={exportPdf} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm font-semibold text-sm hover:border-primary"><Download className="w-4 h-4" /> PDF</button>
          <button onClick={save} data-testid="save-proposal" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm"><Save className="w-4 h-4" /> {saved ? "Guardado" : "Guardar"}</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
        {[["cover", "Portada"], ["style", "Estilo"], ["motion", "Layout & Motion"], ["programs", "Programas"], ["stats", "Data"], ["packages", "Paquetes"], ["internal", "🔒 Interno"], ["closing", "Cierre & Publicar"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive mb-4">{error}</p>}

      {tab === "cover" && (
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Título" value={p.title} onChange={(v) => setF("title", v)} />
          <Field label="Año" value={p.year} onChange={(v) => setF("year", v)} />
          <Field label="Nombre del cliente" value={p.client_name} onChange={(v) => setF("client_name", v)} />
          <Field label="Color de acento (hex)" value={p.theme_accent} placeholder="#FF0055" onChange={(v) => setF("theme_accent", v)} />
          <div className="md:col-span-2"><ImagePicker label="Imagen de portada" value={p.cover_image} onChange={(v) => setF("cover_image", v)} /></div>
          <TextArea label="Texto introductorio" value={p.intro_text} onChange={(v) => setF("intro_text", v)} />
          <Field label="Título de la propuesta (pág. introductoria)" value={p.intro_title} onChange={(v) => setF("intro_title", v)} />
        </div>
      )}

      {tab === "style" && (
        <div className="space-y-8">
          <CustomThemeSelector p={p} setF={setF} />

          <div>
            <p className="text-xs uppercase tracking-widest font-bold mb-3">Modo</p>
            <div className="flex gap-2">
              {[["dark", "Oscuro"], ["light", "Claro"]].map(([v, l]) => (
                <button key={v} onClick={() => setF("theme_mode", v)} data-testid={`mode-${v}`} className={`px-5 py-2 rounded-sm text-sm font-semibold border transition-colors ${(p.theme_mode || "dark") === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{l}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest font-bold mb-3">Fondo de la presentación</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {BG_PRESETS.map((bp) => {
                const selected = (p.bg_preset || "pink-glow") === bp.id;
                const { bgCss } = buildPageStyles({ preset: bp.id, mode: bp.mode, accent: p.theme_accent || "#FF0055", gFrom: p.gradient_from, gTo: p.gradient_to, bgColor: p.bg_color });
                return (
                  <button key={bp.id} type="button" onClick={() => setF("bg_preset", bp.id)} data-testid={`preset-${bp.id}`} className={`group relative rounded-sm overflow-hidden text-left border-2 transition-all ${selected ? "border-primary ring-2 ring-primary/40 scale-[1.02]" : "border-border hover:border-primary/60"}`}>
                    <div className="aspect-[16/10] relative" style={cssToObj(bgCss)}>
                      {bp.id === "cover" && p.cover_image && <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7)), url(${p.cover_image.startsWith("/") ? (process.env.REACT_APP_BACKEND_URL + p.cover_image) : p.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
                      <div className="absolute bottom-2 left-2.5 right-2.5">
                        <p className={`text-[10px] uppercase tracking-widest font-black ${bp.mode === "light" ? "text-black" : "text-white"}`}>{bp.label}</p>
                      </div>
                      {selected && <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-primary ring-2 ring-background" />}
                    </div>
                    <div className="p-2 bg-secondary/30">
                      <p className="text-[10px] text-muted-foreground">{bp.subtitle}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Color acento (hex)" value={p.theme_accent} placeholder="#FF0055" onChange={(v) => setF("theme_accent", v)} />
            <Field label="Color de fondo override (opcional, hex)" value={p.bg_color} placeholder="#050505" onChange={(v) => setF("bg_color", v)} />
            {p.bg_preset === "custom-gradient" && <>
              <Field label="Gradiente · color inicial" value={p.gradient_from} placeholder="#FF0055" onChange={(v) => setF("gradient_from", v)} />
              <Field label="Gradiente · color final" value={p.gradient_to} placeholder="#0EE6CB" onChange={(v) => setF("gradient_to", v)} />
            </>}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Tipografía títulos</label>
              <select data-testid="font-heading" value={p.font_heading || "Unbounded"} onChange={(e) => setF("font_heading", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
                {FONT_HEADINGS.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}
              </select>
              <p className="mt-2 text-2xl font-black" style={{ fontFamily: `'${p.font_heading || "Unbounded"}', system-ui, sans-serif`, letterSpacing: "-0.02em" }}>The quick INFLUCAST</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Tipografía cuerpo</label>
              <select data-testid="font-body" value={p.font_body || "Manrope"} onChange={(e) => setF("font_body", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
                {FONT_BODIES.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>)}
              </select>
              <p className="mt-2 text-base" style={{ fontFamily: `'${p.font_body || "Manrope"}', system-ui, sans-serif` }}>Texto de prueba en cuerpo · Manrope is the brand body.</p>
            </div>
          </div>

          <div>
            <ImagePicker label="Logo (visible en portada y cierre)" value={p.logo_image} onChange={(v) => setF("logo_image", v)} />
          </div>
        </div>
      )}

      {tab === "motion" && <MotionTab p={p} setF={setF} />}

      {tab === "programs" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">Programas ({(p.programs || []).length})</h3>
            <button onClick={() => setP({ ...p, programs: [...(p.programs || []), { name: "", logo: "", schedule: "", description: "", teaser: "", hosts: [] }] })} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm font-semibold hover:border-primary"><Plus className="w-4 h-4" /> Agregar programa</button>
          </div>
          {(p.programs || []).map((prog, i) => (
            <div key={i} className="border border-border rounded-sm p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-display font-bold text-lg">Programa #{i + 1}</span>
                <div className="flex gap-1">
                  <button disabled={i === 0} onClick={() => { const a = [...p.programs]; [a[i-1], a[i]] = [a[i], a[i-1]]; setF("programs", a); }} className="p-1.5 border border-border rounded-sm disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                  <button disabled={i === p.programs.length - 1} onClick={() => { const a = [...p.programs]; [a[i+1], a[i]] = [a[i], a[i+1]]; setF("programs", a); }} className="p-1.5 border border-border rounded-sm disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  <button onClick={() => setF("programs", p.programs.filter((_, idx) => idx !== i))} className="p-1.5 border border-destructive/40 text-destructive rounded-sm"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nombre" value={prog.name} onChange={(v) => { const a = [...p.programs]; a[i] = { ...a[i], name: v }; setF("programs", a); }} />
                <Field label="Horario" value={prog.schedule} placeholder="Lun-Vie 7:00-9:00" onChange={(v) => { const a = [...p.programs]; a[i] = { ...a[i], schedule: v }; setF("programs", a); }} />
                <div className="md:col-span-2"><ImagePicker label="Logo del programa" value={prog.logo} onChange={(v) => { const a = [...p.programs]; a[i] = { ...a[i], logo: v }; setF("programs", a); }} /></div>
                <TextArea label="Descripción / teaser" value={prog.description || prog.teaser || ""} onChange={(v) => { const a = [...p.programs]; a[i] = { ...a[i], description: v }; setF("programs", a); }} />
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-widest font-bold">Conductores</label>
                    <button onClick={() => { const a = [...p.programs]; a[i].hosts = [...(a[i].hosts || []), { name: "", photo: "" }]; setF("programs", a); }} className="text-xs text-primary font-semibold hover:underline">+ Agregar conductor</button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(prog.hosts || []).map((h, hi) => (
                      <div key={hi} className="border border-border rounded-sm p-3">
                        <Field label="Nombre" value={h.name} onChange={(v) => { const a = [...p.programs]; a[i].hosts[hi] = { ...h, name: v }; setF("programs", a); }} />
                        <div className="mt-2"><ImagePicker label="Foto" value={h.photo} onChange={(v) => { const a = [...p.programs]; a[i].hosts[hi] = { ...h, photo: v }; setF("programs", a); }} /></div>
                        <button onClick={() => { const a = [...p.programs]; a[i].hosts = a[i].hosts.filter((_, x) => x !== hi); setF("programs", a); }} className="text-xs text-destructive mt-2 hover:underline">Quitar</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "stats" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">Data general por plataforma</h3>
            <div className="flex gap-2">
              <button onClick={() => setF("stats", DEFAULT_STATS)} className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">Cargar 4 plataformas</button>
              <button onClick={() => setF("stats", [...(p.stats || []), { platform: "", followers: "", views: "", interactions: "" }])} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary"><Plus className="w-3 h-3" /> Fila</button>
            </div>
          </div>
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
                <tr><th className="p-3 text-left">Plataforma</th><th className="p-3 text-left">Followers</th><th className="p-3 text-left">Views</th><th className="p-3 text-left">Interacciones</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {(p.stats || []).map((s, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-2"><input value={s.platform || ""} onChange={(e) => { const a = [...p.stats]; a[i] = { ...s, platform: e.target.value }; setF("stats", a); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" /></td>
                    <td className="p-2"><input value={s.followers || ""} placeholder="+754K" onChange={(e) => { const a = [...p.stats]; a[i] = { ...s, followers: e.target.value }; setF("stats", a); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" /></td>
                    <td className="p-2"><input value={s.views || ""} placeholder="+20M" onChange={(e) => { const a = [...p.stats]; a[i] = { ...s, views: e.target.value }; setF("stats", a); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" /></td>
                    <td className="p-2"><input value={s.interactions || ""} placeholder="+3.8M" onChange={(e) => { const a = [...p.stats]; a[i] = { ...s, interactions: e.target.value }; setF("stats", a); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" /></td>
                    <td className="p-2"><button onClick={() => setF("stats", p.stats.filter((_, x) => x !== i))} className="p-1.5 border border-destructive/40 text-destructive rounded-sm"><Trash2 className="w-3 h-3" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "packages" && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-display font-bold text-xl">Paquetes comerciales ({(p.packages || []).length})</h3>
            <div className="flex gap-2">
              <ImportPackageButton proposalId={id} onImported={(fresh) => setP(fresh)} />
              <button onClick={() => setF("packages", [...(p.packages || []), { name: "", price: "", currency: "BOB", frequency: "mensual", features: [], exclusivity: false, featured: false }])} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm font-semibold hover:border-primary"><Plus className="w-4 h-4" /> Nuevo paquete</button>
            </div>
          </div>
          {(p.packages || []).map((pk, i) => (
            <div key={i} className="border border-border rounded-sm p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-display font-bold text-lg">Paquete #{i + 1}</span>
                <div className="flex gap-1">
                  <button disabled={i === 0} onClick={() => { const a = [...p.packages]; [a[i-1], a[i]] = [a[i], a[i-1]]; setF("packages", a); }} className="p-1.5 border border-border rounded-sm disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                  <button disabled={i === p.packages.length - 1} onClick={() => { const a = [...p.packages]; [a[i+1], a[i]] = [a[i], a[i+1]]; setF("packages", a); }} className="p-1.5 border border-border rounded-sm disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  <button onClick={() => setF("packages", p.packages.filter((_, x) => x !== i))} className="p-1.5 border border-destructive/40 text-destructive rounded-sm"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Nombre" value={pk.name} onChange={(v) => { const a = [...p.packages]; a[i] = { ...pk, name: v }; setF("packages", a); }} />
                <Field label="Precio" value={pk.price} placeholder="3500" onChange={(v) => { const a = [...p.packages]; a[i] = { ...pk, price: v }; setF("packages", a); }} />
                <Field label="Moneda" value={pk.currency || "USD"} onChange={(v) => { const a = [...p.packages]; a[i] = { ...pk, currency: v }; setF("packages", a); }} />
                <Field label="Frecuencia" value={pk.frequency} placeholder="mensual" onChange={(v) => { const a = [...p.packages]; a[i] = { ...pk, frequency: v }; setF("packages", a); }} />
                <label className="flex items-center gap-2 h-10 mt-6"><input type="checkbox" checked={!!pk.exclusivity} onChange={(e) => { const a = [...p.packages]; a[i] = { ...pk, exclusivity: e.target.checked }; setF("packages", a); }} className="accent-primary" /> Exclusividad de categoría</label>
                <label className="flex items-center gap-2 h-10 mt-6"><input type="checkbox" checked={!!pk.featured} onChange={(e) => { const a = [...p.packages]; a[i] = { ...pk, featured: e.target.checked }; setF("packages", a); }} className="accent-primary" /> Destacado</label>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-widest font-bold">Features / incluye</label>
                  <button onClick={() => { const a = [...p.packages]; a[i].features = [...(a[i].features || []), ""]; setF("packages", a); }} className="text-xs text-primary font-semibold hover:underline">+ Agregar</button>
                </div>
                {(pk.features || []).map((f, fi) => (
                  <div key={fi} className="flex gap-2 mb-2">
                    <input value={f} onChange={(e) => { const a = [...p.packages]; a[i].features[fi] = e.target.value; setF("packages", a); }} className="flex-1 h-9 px-3 bg-background border border-border rounded-sm text-sm" />
                    <button onClick={() => { const a = [...p.packages]; a[i].features = a[i].features.filter((_, x) => x !== fi); setF("packages", a); }} className="p-1.5 border border-destructive/40 text-destructive rounded-sm"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "internal" && <InternalTab p={p} setF={setF} />}

      {tab === "closing" && (
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
          <Field label="Texto de cierre" value={p.closing_text} onChange={(v) => setF("closing_text", v)} />
          <label className="flex items-center gap-2 h-10 mt-6">
            <input type="checkbox" checked={!!p.published} onChange={(e) => setF("published", e.target.checked)} className="accent-primary w-4 h-4" />
            <span className="font-semibold">Publicar (hace accesible la vista pública en /propuesta/{p.slug})</span>
          </label>
          {p.published && <div className="md:col-span-2 p-4 border border-primary rounded-sm bg-primary/5 text-sm">
            Link público: <code className="text-primary">{window.location.origin}/propuesta/{p.slug}</code> — podés compartirlo por email o WhatsApp con el cliente.
          </div>}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{label}</label>
      <input type={type} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
    </div>
  );
}
function TextArea({ label, value, onChange }) {
  return (
    <div className="md:col-span-2">
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
    </div>
  );
}

function cssToObj(cssText) {
  const obj = {};
  cssText.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const k = decl.slice(0, idx).trim();
    const v = decl.slice(idx + 1).trim();
    if (!k || !v) return;
    const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = v;
  });
  return obj;
}

const SIZE_OPTIONS = [
  { v: "small", l: "Chico" },
  { v: "medium", l: "Mediano" },
  { v: "large", l: "Grande" },
  { v: "xl", l: "Extra grande" },
];

function CustomThemeSelector({ p, setF }) {
  const [themes, setThemes] = useState([]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  useEffect(() => {
    import("@/lib/api").then(({ api }) => api.get("/admin/proposal-themes").then(setThemes).catch(() => setThemes([])));
  }, []);
  const selected = themes.find((t) => t.id === p.custom_theme_id);
  return (
    <div data-testid="custom-theme-selector">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-xs uppercase tracking-widest font-bold">Plantilla custom (opcional)</p>
        <a href="/admin/proposal-themes" target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest font-bold underline text-primary">Subir nueva</a>
      </div>
      {themes.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <button onClick={() => setF("custom_theme_id", "")} className={`relative aspect-video rounded-sm overflow-hidden border-2 ${!p.custom_theme_id ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/60"}`} data-testid="theme-none">
            <div className="absolute inset-0 bg-secondary/30 flex items-center justify-center text-xs uppercase tracking-widest font-bold text-muted-foreground">Sin plantilla</div>
          </button>
          <p className="col-span-full text-xs text-muted-foreground italic mt-2">No tenés plantillas custom todavía. <a href="/admin/proposal-themes" target="_blank" rel="noopener noreferrer" className="text-primary underline">Subí un PDF</a> para usarlo como layout.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <button onClick={() => setF("custom_theme_id", "")} className={`relative aspect-video rounded-sm overflow-hidden border-2 ${!p.custom_theme_id ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/60"}`} data-testid="theme-none">
              <div className="absolute inset-0 bg-secondary/30 flex items-center justify-center text-xs uppercase tracking-widest font-bold text-muted-foreground">Sin plantilla</div>
            </button>
            {themes.map((t) => (
              <button key={t.id} onClick={() => {
                setF("custom_theme_id", t.id);
                if ((t.detected_accent || t.detected_bg) && window.confirm("¿Aplicar también los colores detectados de la plantilla?\n\nAccent: " + t.detected_accent + "\nFondo: " + t.detected_bg)) {
                  if (t.detected_accent) setF("theme_accent", t.detected_accent);
                  if (t.detected_bg) setF("bg_color", t.detected_bg);
                }
              }} className={`relative aspect-video rounded-sm overflow-hidden border-2 ${p.custom_theme_id === t.id ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/60"}`} data-testid={`theme-${t.id}`}>
                {t.pages?.[0] && <img src={`${BACKEND}/api/media/${t.pages[0].media_id}`} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-white truncate">{t.name}</p>
                </div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="mt-3 p-3 border border-primary/30 rounded-sm bg-primary/5">
              <p className="text-xs"><strong>{selected.name}</strong> · {selected.pages?.length || 0} páginas detectadas. Se renderizan como capa de fondo en cada sección de la propuesta.</p>
              <div className="mt-2 flex gap-2 items-center text-[10px]">
                <span className="text-muted-foreground">Modo:</span>
                {[{ k: "background", l: "Solo fondo" }, { k: "overlay", l: "Capa overlay" }].map((m) => (
                  <button key={m.k} onClick={() => setF("custom_theme_mode", m.k)} className={`px-2 py-1 rounded-sm border ${(p.custom_theme_mode || "background") === m.k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`theme-mode-${m.k}`}>{m.l}</button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function MotionTab({ p, setF }) {
  return (
    <div className="space-y-8 max-w-3xl" data-testid="motion-tab">
      <section>
        <p className="text-xs uppercase tracking-widest font-bold mb-3">Tamaños</p>
        <div className="grid md:grid-cols-2 gap-4">
          <SizePicker label="Imagen de portada" value={p.cover_size} onChange={(v) => setF("cover_size", v)} options={SIZE_OPTIONS} testid="size-cover" />
          <SizePicker label="Logo de marca" value={p.logo_size} onChange={(v) => setF("logo_size", v)} options={SIZE_OPTIONS.slice(0, 3)} testid="size-logo" />
          <SizePicker label="Logos de programas" value={p.program_logo_size} onChange={(v) => setF("program_logo_size", v)} options={SIZE_OPTIONS.slice(0, 3)} testid="size-program-logo" />
          <SizePicker label="Fotos de conductores" value={p.host_photo_size} onChange={(v) => setF("host_photo_size", v)} options={SIZE_OPTIONS.slice(0, 3)} testid="size-host-photo" />
        </div>
      </section>

      <section>
        <p className="text-xs uppercase tracking-widest font-bold mb-3">Animaciones</p>
        <div className="flex gap-2 flex-wrap" data-testid="animation-picker">
          {[{ v: "none", l: "Sin animación", d: "Estático, ideal para imprimir" }, { v: "subtle", l: "Sutil", d: "Fade-in suave (default)" }, { v: "dynamic", l: "Dinámico", d: "Slide + scale + parallax" }].map((o) => (
            <button key={o.v} onClick={() => setF("animation_intensity", o.v)} data-testid={`anim-${o.v}`} className={`text-left p-3 rounded-sm border w-full md:w-1/3 transition-all ${(p.animation_intensity || "subtle") === o.v ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/60"}`}>
              <p className="font-display font-bold">{o.l}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{o.d}</p>
            </button>
          ))}
        </div>
      </section>

      <section>
        <p className="text-xs uppercase tracking-widest font-bold mb-3">Capas decorativas</p>
        <label className="flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer hover:border-primary">
          <input type="checkbox" checked={p.layer_glow !== false} onChange={(e) => setF("layer_glow", e.target.checked)} className="accent-primary w-4 h-4" data-testid="layer-glow" />
          <div>
            <p className="font-display font-bold text-sm">Glow de fondo (orbs blur)</p>
            <p className="text-xs text-muted-foreground">Activá los orbs de color que dan profundidad a portada y cierre.</p>
          </div>
        </label>
      </section>
    </div>
  );
}

function SizePicker({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{label}</label>
      <div className="flex gap-1" data-testid={testid}>
        {options.map((o) => (
          <button key={o.v} onClick={() => onChange(o.v)} className={`flex-1 px-3 py-2 text-xs font-semibold rounded-sm border ${value === o.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{o.l}</button>
        ))}
      </div>
    </div>
  );
}

function ImportPackageButton({ proposalId, onImported }) {
  const [open, setOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("");
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      import("@/lib/api").then(({ api }) => {
        api.get("/admin/com/packages").then(setPackages).catch(() => setPackages([]));
        api.get("/admin/com/products").then(setProducts).catch(() => setProducts([]));
      });
    }
  }, [open]);
  const doImport = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { api } = await import("@/lib/api");
      const fresh = await api.post(`/admin/proposals/${proposalId}/import-from-package/${selected}?mode=${mode}`, {});
      onImported(fresh);
      setOpen(false); setSelected(""); setMode("append");
    } catch (e) { alert("Error al importar: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const pkg = packages.find((x) => x.id === selected);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="import-package-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-sm text-sm font-semibold hover:border-primary">
        <Plus className="w-4 h-4" /> Importar desde paquete CMS
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-background border border-border rounded-sm w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="font-display font-bold text-xl">Importar paquete del CMS</h3>
              <button onClick={() => setOpen(false)} className="text-2xl leading-none">×</button>
            </div>
            <div className="grid md:grid-cols-2 divide-x divide-border">
              {/* LEFT — selector + paquetes */}
              <div className="p-5">
                <label className="text-[10px] uppercase tracking-widest font-bold mb-2 block">Paquetes disponibles ({packages.length})</label>
                <div className="border border-border rounded-sm max-h-[420px] overflow-y-auto">
                  {packages.map((pk) => (
                    <button key={pk.id} onClick={() => setSelected(pk.id)} data-testid={`pkg-option-${pk.id}`}
                      className={`w-full text-left p-3 border-b border-border last:border-b-0 transition-colors ${selected === pk.id ? "bg-primary/10 border-l-4 border-l-primary" : "hover:bg-secondary/40"}`}>
                      <p className="font-display font-bold text-sm">{pk.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{(pk.items || []).length} productos · {pk.duration || "—"}</p>
                      <p className="text-xs font-bold text-primary mt-1">{pk.currency || "BOB"} {(Number(pk.final_price) || 0).toLocaleString()}</p>
                    </button>
                  ))}
                  {packages.length === 0 && <p className="p-6 text-xs italic text-muted-foreground text-center">Sin paquetes en el CMS.</p>}
                </div>
                <label className="text-[10px] uppercase tracking-widest font-bold mt-4 mb-1 block">Modo</label>
                <div className="flex gap-2">
                  <button onClick={() => setMode("append")} className={`flex-1 px-3 py-2 text-xs font-semibold rounded-sm border ${mode === "append" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Agregar al final</button>
                  <button onClick={() => setMode("replace")} className={`flex-1 px-3 py-2 text-xs font-semibold rounded-sm border ${mode === "replace" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Reemplazar todos</button>
                </div>
              </div>
              {/* RIGHT — preview */}
              <div className="p-5 bg-secondary/10">
                <label className="text-[10px] uppercase tracking-widest font-bold mb-2 block">Vista previa</label>
                {!pkg ? (
                  <div className="border border-dashed border-border rounded-sm p-12 text-center text-xs italic text-muted-foreground h-full flex items-center justify-center">
                    Seleccioná un paquete para ver cómo se verá en la propuesta.
                  </div>
                ) : (
                  <div className="border border-primary/30 rounded-sm p-4 bg-background">
                    <p className="text-[10px] uppercase tracking-widest text-primary font-bold">Plan</p>
                    <h4 className="font-display font-black text-2xl tracking-tighter mb-1">{pkg.name}</h4>
                    {pkg.description && <p className="text-xs text-muted-foreground mb-3">{pkg.description}</p>}
                    <p className="font-display font-black text-3xl text-primary mb-4">{pkg.currency || "BOB"} {(Number(pkg.final_price) || 0).toLocaleString()}<span className="text-xs text-muted-foreground ml-2 font-normal">{pkg.duration}</span></p>
                    <p className="text-[10px] uppercase tracking-widest font-bold mb-2 text-muted-foreground">Items que se importarán como features ({(pkg.items || []).length})</p>
                    <ul className="space-y-1">
                      {(pkg.items || []).map((it, i) => {
                        const prod = productMap[it.product_id] || {};
                        const price = it.price_override ?? prod.base_price;
                        return (
                          <li key={i} className="flex items-start gap-2 text-xs border-b border-border last:border-b-0 py-1.5">
                            <span className="text-primary">●</span>
                            <span className="flex-1">
                              <strong>{it.qty || 1}x {prod.name || "Producto"}</strong>
                              {it.frequency && <span className="text-muted-foreground"> · {it.frequency}</span>}
                            </span>
                            {price != null && <span className="text-muted-foreground">{pkg.currency || "BOB"} {Number(price).toLocaleString()}</span>}
                          </li>
                        );
                      })}
                    </ul>
                    {pkg.internal_costs?.length > 0 && (
                      <p className="text-[10px] text-amber-500 mt-3 italic flex items-center gap-1">⚠ {pkg.internal_costs.length} costo(s) interno(s) se copiarán solo si la propuesta no tiene ninguno.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-border">
              <button onClick={() => setOpen(false)} className="px-4 py-2 border border-border rounded-sm text-sm">Cancelar</button>
              <button onClick={doImport} disabled={!selected || busy} data-testid="import-package-confirm" className="px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold disabled:opacity-50">
                {busy ? "Importando..." : (mode === "replace" ? "Reemplazar y importar" : "Agregar a la propuesta")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InternalTab({ p, setF }) {
  const [talents, setTalents] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [reasons, setReasons] = useState({ lost: [], discount: [] });
  useEffect(() => {
    import("@/lib/api").then(({ api }) => {
      api.get("/admin/com/talents").then(setTalents).catch(() => {});
      api.get("/admin/com/clients").then(setClients).catch(() => {});
      api.get("/admin/com/brands").then(setBrands).catch(() => {});
      api.get("/admin/com/agencies").then(setAgencies).catch(() => {});
      api.get("/admin/programs").then(setPrograms).catch(() => {});
      api.get("/admin/com/catalogs/reasons").then(setReasons).catch(() => {});
    });
  }, []);
  const costs = p.internal_costs || [];
  const totalCosts = costs.reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const finalPrice = Number(p.final_price) || 0;
  const net = Math.round((finalPrice - totalCosts) * 100) / 100;
  const margin = finalPrice > 0 ? Math.round((net / finalPrice) * 10000) / 100 : 0;

  const addCost = (type) => {
    const newCost = { id: `c${Date.now()}`, type, description: "", amount: 0, currency: p.currency || "USD", talent_id: "" };
    if (type === "talent" && talents.length) {
      newCost.talent_id = talents[0].id; newCost.amount = Number(talents[0].base_price || 0);
      newCost.description = `Talento: ${talents[0].artist_name}`;
    }
    setF("internal_costs", [...costs, newCost]);
  };
  const update = (i, patch) => { const a = [...costs]; a[i] = { ...a[i], ...patch }; setF("internal_costs", a); };
  const removeCost = (i) => setF("internal_costs", costs.filter((_, x) => x !== i));

  return (
    <div className="max-w-5xl">
      <div className="border-2 border-dashed border-amber-500/40 bg-amber-500/5 rounded-sm p-5 mb-6">
        <p className="text-sm font-semibold flex items-center gap-2 mb-1">🔒 Sección privada · contabilidad interna</p>
        <p className="text-xs text-muted-foreground">Estos datos NO se muestran al cliente, ni en la vista pública, ni en el portal del productor. Solo super admin y comercial los ven.</p>
      </div>

      {/* Vinculaciones CMS */}
      <div className="border border-border rounded-sm p-5 mb-6">
        <h3 className="font-display font-bold mb-3">Vinculaciones CMS</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Cliente</label>
            <select value={p.client_id || ""} onChange={(e) => {
              const c = clients.find((x) => x.id === e.target.value);
              setF("client_id", e.target.value);
              if (c) setF("client_name", c.name);
            }} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
              <option value="">— Ninguno —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Marca</label>
            <select value={p.brand_id || ""} onChange={(e) => setF("brand_id", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
              <option value="">— Ninguna —</option>
              {brands.filter((b) => !p.client_id || b.client_id === p.client_id).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Agencia (opcional)</label>
            <select value={p.agency_id || ""} onChange={(e) => setF("agency_id", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
              <option value="">— Ninguna —</option>
              {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Programa</label>
            <select value={p.program_id || ""} onChange={(e) => setF("program_id", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
              <option value="">— Ninguno —</option>
              {programs.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
            </select>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 italic">Al cerrar la propuesta (status=closed), se crea automáticamente una campaña con estos datos.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Precio final (bruto)</label>
          <input type="number" step="0.01" value={p.final_price || 0} onChange={(e) => setF("final_price", Number(e.target.value))} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Moneda</label>
          <select value={p.currency || "BOB"} onChange={(e) => setF("currency", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
            <option value="BOB">BOB · Boliviano</option>
            <option value="USD">USD · Dólar</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Estado</label>
          <select value={p.status || "draft"} onChange={(e) => {
            const newStatus = e.target.value;
            setF("status", newStatus);
            if (newStatus === "closed" && !p.closed_at) {
              setF("closed_at", new Date().toISOString());
            }
          }} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
            <option value="draft">Borrador</option>
            <option value="sent">Enviada</option>
            <option value="reviewing">En revisión</option>
            <option value="interested">Interesada</option>
            <option value="negotiating">En negociación</option>
            <option value="approved">Aprobada</option>
            <option value="closed">Cerrada (entra al reporte mensual)</option>
            <option value="lost">Perdida</option>
            <option value="paused">Pausada</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>
        {p.status === "lost" && (
          <>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block text-destructive">Motivo de pérdida (requerido)</label>
              <select value={p.lost_reason || ""} onChange={(e) => setF("lost_reason", e.target.value)} className="w-full h-10 px-3 bg-background border border-destructive rounded-sm text-sm">
                <option value="">— Seleccionar motivo —</option>
                {(reasons.lost || []).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Comentario adicional</label>
              <textarea rows={2} value={p.lost_reason_notes || ""} onChange={(e) => setF("lost_reason_notes", e.target.value)} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm resize-none" />
            </div>
          </>
        )}
        {Number(p.discount_pct) > 0 && (
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Motivo de descuento</label>
            <select value={p.discount_reason || ""} onChange={(e) => setF("discount_reason", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
              <option value="">— Seleccionar motivo —</option>
              {(reasons.discount || []).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Fecha de cierre (para reporte mensual)</label>
          <input type="date" value={(p.closed_at || "").slice(0, 10)} onChange={(e) => setF("closed_at", e.target.value ? e.target.value + "T12:00:00Z" : "")} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Descuento %</label>
          <input type="number" step="0.5" value={p.discount_pct || 0} onChange={(e) => setF("discount_pct", Number(e.target.value))} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Forma de pago</label>
          <input value={p.payment_terms || ""} placeholder="50% adelanto / 50% al cierre" onChange={(e) => setF("payment_terms", e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
        </div>
      </div>

      <div className="border border-border rounded-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display font-bold">Costos internos · {costs.length}</h3>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => addCost("talent")} type="button" className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">+ Talento</button>
            <button onClick={() => addCost("viatic")} type="button" className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">+ Viáticos</button>
            <button onClick={() => addCost("production")} type="button" className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">+ Producción</button>
            <button onClick={() => addCost("logistics")} type="button" className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">+ Logística</button>
            <button onClick={() => addCost("equipment")} type="button" className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">+ Equipos</button>
            <button onClick={() => addCost("other")} type="button" className="px-3 py-1.5 border border-border rounded-sm text-xs font-semibold hover:border-primary">+ Otro</button>
          </div>
        </div>
        {costs.length === 0 && <p className="text-xs italic text-muted-foreground">Sin costos cargados.</p>}
        {costs.map((c, i) => (
          <div key={c.id || i} className="flex gap-2 items-end mb-2 border border-border rounded-sm p-2.5 bg-secondary/20">
            <div className="w-32">
              <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Tipo</label>
              <select value={c.type || "other"} onChange={(e) => update(i, { type: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
                <option value="talent">Talento</option><option value="viatic">Viáticos</option>
                <option value="production">Producción</option><option value="logistics">Logística</option>
                <option value="equipment">Equipos</option><option value="other">Otro</option>
              </select>
            </div>
            {c.type === "talent" && (
              <div className="w-44">
                <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Talento</label>
                <select value={c.talent_id || ""} onChange={(e) => {
                  const t = talents.find((x) => x.id === e.target.value);
                  update(i, { talent_id: e.target.value, amount: t ? Number(t.base_price || c.amount || 0) : c.amount, description: t ? `Talento: ${t.artist_name}` : c.description });
                }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
                  <option value="">— Seleccionar —</option>
                  {talents.map((t) => <option key={t.id} value={t.id}>{t.artist_name} · ref {t.currency} {t.base_price}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Descripción</label>
              <input value={c.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div className="w-28">
              <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Monto</label>
              <input type="number" step="0.01" value={c.amount || 0} onChange={(e) => update(i, { amount: Number(e.target.value) })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
            <div className="w-20">
              <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Moneda</label>
              <select value={c.currency || "USD"} onChange={(e) => update(i, { currency: e.target.value })} className="w-full h-9 px-1 bg-background border border-border rounded-sm text-xs">
                <option value="USD">USD</option><option value="BOB">BOB</option>
              </select>
            </div>
            <button onClick={() => removeCost(i)} type="button" className="p-1.5 text-destructive hover:bg-destructive/10 rounded-sm">×</button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-border pt-5">
        <div><p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Bruto</p><p className="font-display font-black text-2xl">{p.currency || "USD"} {finalPrice.toLocaleString()}</p></div>
        <div><p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Costos</p><p className="font-display font-black text-2xl text-amber-500">{p.currency || "USD"} {totalCosts.toLocaleString()}</p></div>
        <div><p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Ganancia neta</p><p className={`font-display font-black text-3xl ${net >= 0 ? "text-emerald-500" : "text-destructive"}`} data-testid="proposal-net">{p.currency || "USD"} {net.toLocaleString()}</p></div>
        <div><p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Margen</p><p className={`font-display font-black text-3xl ${margin >= 0 ? "text-emerald-500" : "text-destructive"}`}>{margin.toFixed(2)}%</p></div>
      </div>

      <div className="mt-5">
        <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Notas internas (privadas)</label>
        <textarea value={p.internal_notes || ""} onChange={(e) => setF("internal_notes", e.target.value)} rows={3} placeholder="Detalles confidenciales solo para tu equipo comercial..." className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
      </div>
    </div>
  );
}

